import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import round from 'lodash/round';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import subHours from 'date-fns/subHours';
import max from 'date-fns/max';
import min from 'date-fns/min';
import Papa from 'papaparse';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import css from 'styled-jsx/css';

import { MONTH_DAY_YEAR } from '../../Constants/DateFormatStrings';
import { DASHBOARD, WIDGET_UPDATE } from '../../Constants/RBAC';
import {
  DAILY,
  ENGINE_HOURS,
  FUEL_USED,
  IDLE_ENGINE_HOURS,
  IDLE_FUEL_USED,
  MONTHLY,
  WEEKLY,
} from '../../Constants/Signal';
import {
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  CAN_SCAN,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import WidgetUtils from '../../Lib/WidgetUtils';
import { STICKY_OVERRIDE } from '../../Styles/Z-INDEX';
import {
  formatLocalForAssetDashboardTile,
  formatUTCForAssetDashboardTile,
} from '../../Utils/dateFormatHelpers.util';

import DeleteWidgetModal from './DeleteWidgetModal';
import EditWidgetModal from './EditWidgetModal';
import ErrorBoundary from '../ErrorBoundary';
import ExportSignalCollectionModal from './ExportSignalCollectionModalContainer';
import UtilizationTimeSelectModal from './UtilizationTimeSelectModal';
import Widget from './Widget';
import Can from '../../RBAC/Can';

import TimeRangeDropdown from './AssetTimeFilter/TimeRangeDropdown';
import CustomTimeFilterModal from './AssetTimeFilter/CustomTimeFilterModal';
import { isTimeFilterVisible } from './AssetTimeFilter/utils';

const { className: dropdownItemClass, styles: dropdownItemStyles } =
  css.resolve`
    button,
    a {
      color: var(--text-light-gray);
      font-size: 0.75rem;
      cursor: pointer;
      box-shadow: none;
      outline: none;
    }
    button:hover {
      background-color: transparent;
    }
    button:focus {
      outline: none;
    }
  `;

const EXPORT_DISABLED = new Set([COMMAND, MAINTENANCE, TEXT_STATIC, TEXT_TAGS]),
  fields = [
    'Timestamp',
    'Total Engine Hours',
    'Active Engine Hours',
    'Idle Engine Hours',
    'Total Fuel Used',
    'Active Fuel Used',
    'Idle Fuel Used',
  ],
  intervalAggregateMap = new Map([
    ['day', DAILY],
    ['month', MONTHLY],
    ['week', WEEKLY],
  ]),
  MAX_DATAPOINTS = 3000,
  SHOW_UPDATED_AT = new Set([
    AREA,
    GAUGE,
    GAUGE_RANGE,
    LINE_GRAPH,
    LINE_HIGH_CHART,
    LOCATION,
    MAP,
    CAN_SCAN,
  ]),
  UI_SERVICE_MAX_DATAPOINTS = 10000;

export default class WidgetDisplayContainer extends PureComponent {
  static propTypes = {
    asset: PropTypes.object,
    dashboardID: PropTypes.string,
    fetchedData: PropTypes.object,
    graphIndex: PropTypes.number,
    graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
    handleCommandExecutedFromWidget: PropTypes.func,
    handleWidgetDeleted: PropTypes.func,
    handleWidgetUpdated: PropTypes.func,
    historicalUtilizationData: PropTypes.object,
    renderNextGraph: PropTypes.func,
    signalNamesMap: PropTypes.object,
    signalUnitsMap: PropTypes.object,
    socket: PropTypes.object,
    updateWidgets: PropTypes.func,
    utilizationData: PropTypes.object,
    widget: PropTypes.object,
    timeFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customStartTimestamp: PropTypes.number,
    customEndTimestamp: PropTypes.number,
    updateRealTime: PropTypes.bool,
    maxDatapoints: PropTypes.number,
  };

  state = {
    customEndTimestamp: null,
    customStartTimestamp: null,
    fetchedData: this.props.fetchedData,
    historicalUtilizationData: this.props.historicalUtilizationData,
    maxDatapoints: this.props.widget?.type === TEXT ? 1 : MAX_DATAPOINTS,
    returningToRealTime: false,
    showCustomFilterModal: false,
    showDeleteWidgetModal: false,
    showEditWidgetModal: false,
    showExportSignalCollectionModal: false,
    showUtilizationTimeSelectModal: false,
    timeFilter: null,
    updateRealTime: true,
  };

  eventHandlerMap = new Map();
  textTagsWidgetRef = createRef();
  widgetUtils = new WidgetUtils();
  isTimeFilterVisible = isTimeFilterVisible(
    this.props.widget.type,
    !!this.props.widget.settings?.displayTextAsGrid
  );

  componentDidMount() {
    this.initializeSocketListeners();
  }

  componentDidUpdate = ({
    fetchedData: prevData,
    historicalUtilizationData: prevHistoricalUtilizationData,
  }) => {
    const { fetchedData = new Map(), historicalUtilizationData = new Map() } =
      this.props;
    if (!isEqual(prevData, fetchedData)) this.setState({ fetchedData });
    if (!isEqual(prevHistoricalUtilizationData, historicalUtilizationData)) {
      this.setState({ historicalUtilizationData });
    }

    //was time filter updated globally?
    const globalTimeFilterDidChange = !isEqual(
        this.props.timeFilter,
        this.state.timeFilter
      ),
      globalCustomStartTimeStampDidChange = !isEqual(
        this.props.customStartTimestamp,
        this.state.customStartTimestamp
      ),
      globalCustomEndTimeStampDidChange = !isEqual(
        this.props.customEndTimestamp,
        this.state.customEndTimestamp
      ),
      globalMaxDatapointsDidChange = !isEqual(
        this.props.maxDatapoints,
        this.state.maxDatapoints
      );
    if (
      (globalTimeFilterDidChange ||
        globalCustomStartTimeStampDidChange ||
        globalCustomEndTimeStampDidChange ||
        globalMaxDatapointsDidChange) &&
      this.isTimeFilterVisible
    ) {
      this.setState(
        {
          customEndTimestamp: this.props.customEndTimestamp,
          customStartTimestamp: this.props.customStartTimestamp,
          timeFilter: this.props.timeFilter,
          maxDatapoints: this.props.maxDatapoints,
        },
        () =>
          this.updateFetchedData(
            this.props.timeFilter,
            this.props.customStartTimestamp,
            this.props.customEndTimestamp
          )
      );
    }
    //was time filter updated globally to return to real time?
    if (
      !isEqual(this.props.updateRealTime, this.state.updateRealTime) &&
      this.isTimeFilterVisible
    ) {
      this.setState({
        updateRealTime: this.props.updateRealTime,
      });
      if (this.props.updateRealTime) {
        this.handleReturnToRealTime();
      }
    }
  };

  componentWillUnmount() {
    this.removeSocketListeners();
  }

  exportHistoricalUtilizationCsv = () => {
    try {
      const targetData =
        this.state.historicalUtilizationData.get(
          this.props.widget?.settings?.aggregation
        ) ?? new Map();

      const data = Array.from(targetData).reduce(
        (acc, [timestamp, signals]) => {
          if (Object.values(signals)?.some(({ max, min }) => !max || !min)) {
            return acc;
          }

          const {
            [ENGINE_HOURS]: totalEngine,
            [FUEL_USED]: totalFuel,
            [IDLE_ENGINE_HOURS]: idleEngine,
            [IDLE_FUEL_USED]: idleFuel,
          } = signals;

          const idleEngineHours = idleEngine?.max - idleEngine?.min,
            totalEngineHours = totalEngine?.max - totalEngine?.min,
            idleFuelUsed = idleFuel?.max - idleFuel?.min,
            totalFuelUsed = totalFuel?.max - totalFuel?.min;

          return acc.concat([
            [
              timestamp,
              round(totalEngineHours, 2),
              round(totalEngineHours - idleEngineHours, 2),
              round(idleEngineHours, 2),
              round(totalFuelUsed, 2),
              round(totalFuelUsed - idleFuelUsed, 2),
              round(idleFuelUsed, 2),
            ],
          ]);
        },
        []
      );

      const csv = Papa.unparse({ data, fields }),
        blob = new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        filename = `${this.props.asset?.nickname}_${this.props.widget?.settings?.title}.csv`,
        link = document.createElement('a'),
        url = URL.createObjectURL(blob);

      link.download = filename;
      link.href = url;
      link.style = 'display: none';

      document.body.appendChild(link);
      link.click();

      window.setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  exportStaticUtilizationCsv = () => {
    try {
      const data = Array.from(this.props.utilizationData).reduce(
        (acc, [timestamp, signals]) => {
          if (Object.values(signals)?.some(({ max, min }) => !max || !min)) {
            return acc;
          }

          const {
            [ENGINE_HOURS]: totalEngine,
            [FUEL_USED]: totalFuel,
            [IDLE_ENGINE_HOURS]: idleEngine,
            [IDLE_FUEL_USED]: idleFuel,
          } = signals;

          const idleEngineHours = idleEngine?.max - idleEngine?.min,
            totalEngineHours = totalEngine?.max - totalEngine?.min,
            idleFuelUsed = idleFuel?.max - idleFuel?.min,
            totalFuelUsed = totalFuel?.max - totalFuel?.min;

          return acc.concat([
            [
              timestamp.charAt(0).toUpperCase().concat(timestamp.slice(1)),
              round(totalEngineHours, 2),
              round(totalEngineHours - idleEngineHours, 2),
              round(idleEngineHours, 2),
              round(totalFuelUsed, 2),
              round(totalFuelUsed - idleFuelUsed, 2),
              round(idleFuelUsed, 2),
            ],
          ]);
        },
        []
      );

      const csv = Papa.unparse({ data, fields }),
        blob = new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        filename = `${this.props.asset?.nickname}_${
          this.props.widget?.settings?.title
        }_${format(new Date(), MONTH_DAY_YEAR)}.csv`,
        link = document.createElement('a'),
        url = URL.createObjectURL(blob);

      link.download = filename;
      link.href = url;
      link.style = 'display: none';

      document.body.appendChild(link);
      link.click();

      window.setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  fetchCustomRangeUtilizationData = ({ dateStart, dateStop, interval }) => {
    this.props.socket?.emit?.(
      'utilization:report',
      { dateStart, dateStop, interval },
      (data) => {
        const dailyData = new Map(
          Object.entries(data || {}).reduce(
            (map, [dateStr, signals]) => map.set(dateStr, signals),
            new Map()
          )
        );
        const historicalUtilizationData = new Map(
          this.state.historicalUtilizationData
        ).set(intervalAggregateMap.get(interval), dailyData);
        this.setState({ historicalUtilizationData });
      }
    );
  };

  getTimeRange = () => {
    const {
        props: { widget: { settings, type } = {} },
        state: { fetchedData, timeFilter },
      } = this,
      isTextGridTile = type === TEXT && !!settings?.displayTextAsGrid;
    let showUpdatedAt = SHOW_UPDATED_AT.has(type) || isTextGridTile;
    let firstDatapoint = '',
      updatedAt = '';
    if (!this.state.fetchedData.size) {
      return { firstDatapoint, showUpdatedAt: false, updatedAt };
    }
    if (!showUpdatedAt) return { firstDatapoint, showUpdatedAt, updatedAt };

    const {
        datapointID,
        lines = [],
        text = [],
        type: signalType,
      } = settings || {},
      data = fetchedData.get(datapointID) || [];

    switch (type) {
      case LOCATION: {
        if (!data?.length) {
          return { firstDatapoint: '', showUpdatedAt: false, updatedAt: '' };
        }
        // Order of data coming in changes depending on whether a timefilter is specified or not
        const [first, last] = timeFilter
          ? [data[0].createdAt, data[data.length - 1].createdAt]
          : [data[data.length - 1].createdAt, data[0].createdAt];

        firstDatapoint = formatLocalForAssetDashboardTile(new Date(first));
        updatedAt = formatLocalForAssetDashboardTile(new Date(last));
        if (!firstDatapoint && !updatedAt) showUpdatedAt = false;

        break;
      }
      case AREA:
      case LINE_GRAPH: {
        const allOutputSignals = lines?.every?.(
            ({ type } = {}) => type === 'outputSignal'
          ),
          eventDates = lines.reduce((timestamps, { datapointID }) => {
            const signalData = fetchedData.get(datapointID);
            return signalData?.length
              ? signalData.reduce((acc, { createdAt } = {}) => {
                  const date = new Date(createdAt);
                  return isValid(date) ? acc.concat(date) : acc;
                }, timestamps)
              : timestamps;
          }, []),
          firstDate = min(eventDates),
          lastDate = max(eventDates);

        firstDatapoint = allOutputSignals
          ? formatUTCForAssetDashboardTile(firstDate)
          : formatLocalForAssetDashboardTile(firstDate);
        updatedAt = allOutputSignals
          ? formatUTCForAssetDashboardTile(lastDate)
          : formatLocalForAssetDashboardTile(lastDate);
        if (!firstDatapoint && !updatedAt) showUpdatedAt = false;

        break;
      }
      case GAUGE:
      case GAUGE_RANGE: {
        const { secondGauge: { datapointID: secondDpID } = {} } =
          settings || {};
        if (!secondDpID || datapointID === secondDpID) {
          if (!data[0]?.createdAt) {
            return { firstDatapoint: '', showUpdatedAt: false, updatedAt: '' };
          }

          updatedAt =
            signalType === 'outputSignal'
              ? formatUTCForAssetDashboardTile(new Date(data[0]?.createdAt))
              : formatLocalForAssetDashboardTile(new Date(data[0]?.createdAt));
          break;
        }
        const secondGaugeData = fetchedData.get(secondDpID) || [];
        const mostRecent = max([
          new Date(data[0]?.createdAt),
          new Date(secondGaugeData[0]?.createdAt),
        ]);
        updatedAt =
          signalType === 'outputSignal'
            ? formatUTCForAssetDashboardTile(mostRecent)
            : formatLocalForAssetDashboardTile(mostRecent);
        if (!updatedAt) showUpdatedAt = false;

        break;
      }
      case CAN_SCAN:
        updatedAt = Array.isArray(settings?.scan)
          ? settings.scan.reduce((formatted, { datapointID }) => {
              const [{ createdAt } = {}] = fetchedData.get(datapointID) || [];
              return createdAt
                ? formatLocalForAssetDashboardTile(new Date(createdAt))
                : '';
            }, '')
          : '';
        if (!updatedAt) showUpdatedAt = false;

        break;
      default:
        if (isTextGridTile) {
          const eventDates = text.reduce((timestamps, { datapointID }) => {
              const signalData = fetchedData.get(datapointID);
              if (!Array.isArray(signalData) || !signalData.length) {
                return timestamps;
              }

              return signalData.reduce(
                (acc, { createdAt } = {}) =>
                  createdAt ? acc.concat(new Date(createdAt)) : acc,
                timestamps
              );
            }, []),
            latestEvent = max(eventDates);

          updatedAt = formatLocalForAssetDashboardTile(latestEvent);
          if (!updatedAt) showUpdatedAt = false;

          break;
        }

        if (!data[0]?.createdAt) {
          return { firstDatapoint: '', showUpdatedAt: false, updatedAt: '' };
        }

        updatedAt =
          signalType === 'outputSignal'
            ? formatUTCForAssetDashboardTile(new Date(data[0]?.createdAt))
            : formatLocalForAssetDashboardTile(new Date(data[0]?.createdAt));
        if (!updatedAt) showUpdatedAt = false;

        break;
    }

    return { showUpdatedAt, firstDatapoint, updatedAt };
  };

  handleCustomFilterSaveCallback = ({
    customEndTimestamp,
    maxDatapoints,
    customStartTimestamp,
  }) => {
    this.setState(
      {
        customEndTimestamp,
        customStartTimestamp,
        maxDatapoints,
        showCustomFilterModal: false,
        timeFilter: 'custom',
      },
      () =>
        this.updateFetchedData(
          'custom',
          customStartTimestamp,
          customEndTimestamp
        )
    );
  };

  handleExportClick = () => {
    switch (this.props.widget?.type) {
      case BAR_ENGINE_UTILIZATION:
      case BAR_FUEL_UTILIZATION:
        return this.exportHistoricalUtilizationCsv();
      case TEXT_ENGINE:
      case TEXT_FUEL:
        return this.exportStaticUtilizationCsv();
      default:
        return this.toggleExportSignalCollectionModal();
    }
  };

  handleRealtimeData = (key) => {
    const eventHandler = (newData) => {
      if (this.props.socket?.disconnected) return;
      if (!this.state.updateRealTime) return;

      const [, datapointID] = key.split(':'),
        currentData = JSON.parse(
          JSON.stringify([...(this.state.fetchedData.get(datapointID) || [])])
        );
      if (currentData?.length >= this.state.maxDatapoints) currentData.pop();

      currentData.unshift(newData);
      const fetchedData = new Map([
        ...this.state.fetchedData,
        [datapointID, currentData],
      ]);
      this.setState({ fetchedData });
    };
    this.eventHandlerMap.set(key, eventHandler);
    return eventHandler;
  };

  handleReturnToRealTime = async () => {
    this.setState(
      {
        customEndTimestamp: null,
        customStartTimestamp: null,
        fetchedData: new Map(),
        returningToRealTime: true,
        timeFilter: null,
        updateRealTime: true,
      },
      this.refreshFetchedData
    );
  };

  handleTimeRangeFilter = (timeFilter) => {
    if (timeFilter === 'custom') {
      return this.setState({ showCustomFilterModal: true });
    }

    const customEndTimestamp = Date.now(),
      customStartTimestamp = subHours(customEndTimestamp, timeFilter).getTime();

    this.setState(
      { customEndTimestamp, customStartTimestamp, timeFilter },
      () =>
        this.updateFetchedData(
          timeFilter,
          customStartTimestamp,
          customEndTimestamp
        )
    );
  };

  handleUtilizationTimeSelect = (dateStart, dateStop, interval) => {
    this.setState(
      {
        customEndTimestamp: dateStop.getTime(),
        customStartTimestamp: dateStart.getTime(),
        showUtilizationTimeSelectModal: false,
      },
      () => {
        this.fetchCustomRangeUtilizationData({
          dateStart: dateStart.toISOString(),
          dateStop: dateStop.toISOString(),
          interval,
        });
      }
    );
  };

  initializeSocketListeners() {
    const keySet = this.widgetUtils.buildRealtimeSubscriptionKeySet(
      this.props.asset?._id,
      this.props.widget
    );
    if (!keySet.size) return;

    keySet.forEach((key) => {
      this.props.socket?.on?.(`realtime:${key}`, this.handleRealtimeData(key));
    });
  }

  refreshFetchedData = () => {
    this.props.socket?.emit?.(
      'dashboard:initial',
      { dashboardID: this.props.dashboardID },
      (data) => {
        const fetchedData = Object.keys(data || {})?.length
          ? new Map(Object.entries(data).filter(([key]) => key !== 'undefined'))
          : new Map();

        this.setState({ fetchedData, returningToRealTime: false });
      }
    );
  };

  removeSocketListeners = () => {
    this.eventHandlerMap.forEach((handler, key) => {
      this.props.socket?.off?.(`realtime:${key}`, handler);
    });
  };

  toggleCustomFilterModal = () => {
    this.setState({
      showCustomFilterModal: !this.state.showCustomFilterModal,
    });
  };

  toggleDeleteWidgetModal = () => {
    this.setState({
      showDeleteWidgetModal: !this.state.showDeleteWidgetModal,
    });
  };

  toggleEditWidgetModal = () => {
    this.setState({ showEditWidgetModal: !this.state.showEditWidgetModal });
  };

  toggleExportSignalCollectionModal = () => {
    this.setState({
      showExportSignalCollectionModal:
        !this.state.showExportSignalCollectionModal,
    });
  };

  toggleUtilizationTimeSelectModal = () => {
    this.setState({
      showUtilizationTimeSelectModal:
        !this.state.showUtilizationTimeSelectModal,
    });
  };

  updateFetchedData = async (timeFilter, dateStart, dateStop) => {
    const datapointIDs = [
        ...this.widgetUtils.buildRealtimeSubscriptionKeySet(
          this.props.asset?._id,
          this.props.widget
        ),
      ].map((key = '') => key?.split?.(':')[1]),
      limit =
        timeFilter === 24
          ? UI_SERVICE_MAX_DATAPOINTS
          : this.state.maxDatapoints,
      order = this.props.widget?.type === TEXT ? 'desc' : 'asc';

    this.props.socket?.emit(
      'widget:events',
      { datapointIDs, dateStart, dateStop, order, limit },
      (data) => {
        if (data?.error) return this.setState({ error: data.error.message });
        const fetchedData = new Map(Object.entries(data || {}));
        this.setState({ fetchedData, updateRealTime: false });
      }
    );
  };

  render() {
    const {
      asset,
      dashboardID,
      graphIndex,
      graphWidgetIDs,
      handleCommandExecutedFromWidget,
      handleWidgetDeleted,
      handleWidgetUpdated,
      renderNextGraph,
      signalNamesMap,
      signalUnitsMap,
      utilizationData,
      widget,
      widget: { _id: widgetID, settings, type } = {},
    } = this.props;
    const {
      customEndTimestamp,
      customStartTimestamp,
      fetchedData,
      historicalUtilizationData,
      maxDatapoints,
      returningToRealTime,
      showCustomFilterModal,
      showDeleteWidgetModal,
      showEditWidgetModal,
      showExportSignalCollectionModal,
      showUtilizationTimeSelectModal,
      timeFilter,
      updateRealTime,
    } = this.state;

    const { firstDatapoint, showUpdatedAt, updatedAt } = this.getTimeRange();

    return (
      <div className="h-100 w-100 widget-outer-wrapper">
        <div className="header-container h-100 w-100 position-relative">
          <div className="position-absolute h-100 w-100 d-flex align-items-center flex-nowrap border-bottom">
            <div className="flex-grow-1 m-0 pl-3 overflow-auto">
              <h6
                className="text-muted text-truncate m-0"
                data-for={`${widgetID}-title`}
                data-tip={widget.settings?.title}
              >
                {widget.settings?.title}
                <ReactTooltip id={`${widgetID}-title`} />
              </h6>
              <div
                className={`${
                  !showUpdatedAt ? 'd-none' : 'd-none d-md-flex'
                } row text-secondary-gray three-quarter-text pl-3`}
              >
                {`updated ${
                  !firstDatapoint
                    ? `at ${updatedAt}`
                    : `from ${firstDatapoint} to ${updatedAt}`
                }`}
              </div>
            </div>
            <div className="flex-grow-0">
              <TimeRangeDropdown
                timeFilter={timeFilter}
                dropdownItemClass={dropdownItemClass}
                displayTextAsGrid={!!settings?.displayTextAsGrid}
                type={type}
                updateRealTime={updateRealTime}
                toggleUtilizationTimeSelectModal={
                  this.toggleUtilizationTimeSelectModal
                }
                handleReturnToRealTime={this.handleReturnToRealTime}
                handleTimeRangeFilter={this.handleTimeRangeFilter}
              />
            </div>
            <div className="flex-grow-0">
              <Can
                no={(data) => (
                  <UncontrolledDropdown>
                    <DropdownToggle
                      color="link"
                      className="py-0 three-quarter-text shadow-none"
                      data-for={widgetID}
                      data-tip={data}
                    >
                      <FontAwesomeIcon
                        icon={faEllipsisH}
                        className="text-muted"
                      />
                    </DropdownToggle>
                    <DropdownMenu
                      right
                      className="rounded-lg shadow"
                      style={{ zIndex: STICKY_OVERRIDE }}
                    >
                      <DropdownItem
                        className={dropdownItemClass}
                        disabled={EXPORT_DISABLED.has(type)}
                        onClick={this.handleExportClick}
                      >
                        Export
                      </DropdownItem>
                    </DropdownMenu>
                    <ReactTooltip id={widgetID} />
                  </UncontrolledDropdown>
                )}
                orgID={asset?.orgID}
                perform={WIDGET_UPDATE}
                resource={DASHBOARD}
                yes={() => (
                  <UncontrolledDropdown>
                    <DropdownToggle
                      color="link"
                      className="py-0 three-quarter-text shadow-none"
                    >
                      <FontAwesomeIcon
                        icon={faEllipsisH}
                        className="text-muted"
                      />
                    </DropdownToggle>
                    <DropdownMenu
                      right
                      className="rounded-lg shadow"
                      style={{ zIndex: STICKY_OVERRIDE }}
                    >
                      <DropdownItem
                        className={dropdownItemClass}
                        onClick={this.toggleEditWidgetModal}
                      >
                        Edit
                      </DropdownItem>
                      <DropdownItem
                        className={dropdownItemClass}
                        disabled={EXPORT_DISABLED.has(type)}
                        onClick={this.handleExportClick}
                      >
                        Export
                      </DropdownItem>
                      <DropdownItem
                        className={`${dropdownItemClass} text-danger`}
                        onClick={this.toggleDeleteWidgetModal}
                      >
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                )}
              />
            </div>
            {dropdownItemStyles}
          </div>
        </div>
        <div className="main-content-container h-100 w-100 position-relative">
          <ErrorBoundary>
            <Widget
              asset={asset}
              fetchedData={fetchedData}
              graphIndex={graphIndex}
              graphWidgetIDs={graphWidgetIDs}
              handleCommandExecutedFromWidget={handleCommandExecutedFromWidget}
              historicalUtilizationData={historicalUtilizationData}
              renderNextGraph={renderNextGraph}
              returningToRealTime={returningToRealTime}
              signalNamesMap={signalNamesMap}
              signalUnitsMap={signalUnitsMap}
              textTagsWidgetRef={this.textTagsWidgetRef}
              updateRealTime={updateRealTime}
              utilizationData={utilizationData}
              widget={widget}
            />
          </ErrorBoundary>
        </div>
        {showCustomFilterModal ? (
          <CustomTimeFilterModal
            customEndTimestamp={customEndTimestamp}
            customStartTimestamp={customStartTimestamp}
            type={type}
            maxDatapoints={maxDatapoints}
            showCustomFilterModal={showCustomFilterModal}
            handleCustomFilterSaveCallback={this.handleCustomFilterSaveCallback}
            toggleCustomFilterModal={this.toggleCustomFilterModal}
          />
        ) : null}
        {showDeleteWidgetModal ? (
          <DeleteWidgetModal
            asset={asset}
            dashboardID={dashboardID}
            isOpen={showDeleteWidgetModal}
            handleWidgetDeleted={handleWidgetDeleted}
            toggleModal={this.toggleDeleteWidgetModal}
            widget={widget}
          />
        ) : null}
        {showEditWidgetModal ? (
          <EditWidgetModal
            asset={asset}
            dashboardID={dashboardID}
            isOpen={showEditWidgetModal}
            handleWidgetUpdated={handleWidgetUpdated}
            toggleModal={this.toggleEditWidgetModal}
            widget={widget}
          />
        ) : null}
        {showExportSignalCollectionModal ? (
          <ExportSignalCollectionModal
            gatewayID={asset?._id}
            isOpen={showExportSignalCollectionModal}
            orgID={asset?.orgID}
            toggleModal={this.toggleExportSignalCollectionModal}
            widget={widget}
          />
        ) : null}
        {showUtilizationTimeSelectModal ? (
          <UtilizationTimeSelectModal
            customEndTimestamp={customEndTimestamp}
            customStartTimestamp={customStartTimestamp}
            handleUtilizationTimeSelect={this.handleUtilizationTimeSelect}
            isOpen={showUtilizationTimeSelectModal}
            settings={settings}
            toggleModal={this.toggleUtilizationTimeSelectModal}
          />
        ) : null}
        <style jsx>{`
          .widget-outer-wrapper {
            display: grid;
            grid-template-rows: 4rem minmax(auto, auto);
            grid-template-areas: 'header' 'content';
          }
          .header-container {
            grid-area: header;
          }
          .main-content-container {
            grid-area: content;
          }
          @media (min-width: 768px) {
            .widget-outer-wrapper {
              grid-template-rows: 3rem minmax(auto, auto);
            }
          }
        `}</style>
      </div>
    );
  }
}
